import { Box, Button, Chip, Tab, Tabs, Typography } from '@mui/material'
import type { DateTime } from 'luxon'
import type { SyntheticEvent } from 'react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { ActivationGroup } from '@/features/activationGroup/types'
import { usePermissions } from '@/features/authorization/contexts/PermissionsContext'
import type { FormattedRow } from '@/features/bessDashboard/types'
import CreateBidsButton from '@/features/bidding/components/cascading/review/CreateBidsButton'
import DirectionBidsSection from '@/features/bidding/components/cascading/review/DirectionBidsSection'
import type { MarketProgram } from '@/features/bidding/constants'
import { CASCADE_MARKETS } from '@/features/bidding/constants'
import { useBidConflictsValidations } from '@/features/bidding/hooks/useBidConflictsValidations'
import type { ExtendedBid } from '@/features/bidding/types/bid'
import type { BidLevelInput } from '@/features/bidding/types/bidLadderInput'
import type { PricingStrategyConfig } from '@/features/bidding/types/forecastPricing'
import type { CascadeMarketId } from '@/features/bidding/types/market'
import { getDirectionFromMarketProgram } from '@/features/bidding/utils/bidLadderInput/getDirectionFromMarketProgram'
import { transformBidLevelsToExtendedBids } from '@/features/bidding/utils/cascading/bidLadder/transformBidLevelsToExtendedBids'
import type { MarketDate } from '@/features/bidding/utils/date/marketDate'

type ReviewBidsContentProps = {
  levels: BidLevelInput[]
  deliveryDay: MarketDate
  activationGroupsMap: Map<string, ActivationGroup>
  /** Pricing strategy config for forecast-based pricing */
  pricingConfig?: PricingStrategyConfig
  /** Forecast data for deriving prices when in forecast mode */
  forecastsData?: FormattedRow[]
  /** Selected delivery day for forecast price derivation */
  selectedDeliveryDay?: DateTime
  /** The currently selected market (e.g., 'mfrr', 'fcr-d') */
  selectedMarket: CascadeMarketId
  /** Excluded PTU groups for per-hour volume zeroing */
  excludedPtuGroups?: Set<string>
  onBackClick: () => void
  onSuccess: () => void
}

function ReviewBidsContent({
  levels,
  deliveryDay,
  activationGroupsMap,
  pricingConfig,
  forecastsData,
  selectedDeliveryDay,
  selectedMarket,
  excludedPtuGroups,
  onBackClick,
  onSuccess,
}: Readonly<ReviewBidsContentProps>) {
  const { t } = useTranslation()
  const { permissions } = usePermissions()
  const canMutateBidding = permissions.has('biddingMutation')
  const [activeTab, setActiveTab] = useState(0)

  const marketLabel = CASCADE_MARKETS.find((m) => m.id === selectedMarket)?.label ?? selectedMarket

  const { bids, errors } = useMemo(
    () =>
      transformBidLevelsToExtendedBids({
        levels,
        deliveryDay,
        activationGroupsMap,
        pricingConfig,
        forecastsData,
        selectedDeliveryDay,
        excludedPtuGroups,
      }),
    [levels, deliveryDay, activationGroupsMap, pricingConfig, forecastsData, selectedDeliveryDay, excludedPtuGroups],
  )

  // Filter bids by direction using the market program to determine up/down
  const { upBids, downBids } = useMemo(() => {
    const up: ExtendedBid[] = []
    const down: ExtendedBid[] = []
    for (const bid of bids) {
      if (getDirectionFromMarketProgram(bid.marketProgram as MarketProgram) === 'up') {
        up.push(bid)
      } else {
        down.push(bid)
      }
    }
    return { upBids: up, downBids: down }
  }, [bids])

  const hasErrors = errors.length > 0
  const currentBids = activeTab === 0 ? upBids : downBids
  const currentDirection = activeTab === 0 ? 'up' : 'down'

  // detect any potential conflicts with existing bids and show a warning if any
  const { isValidating } = useBidConflictsValidations({
    activationGroupsMap,
    upBids,
    downBids,
  })

  const handleTabChange = (_: SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: 'background.paper',
      }}
    >
      {/* Header with title, market chip, and delivery day */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 3,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography component="h2" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
          {t('bidding.bidding_cascading.review_bids.title')}
        </Typography>
        <Chip
          label={marketLabel}
          size="small"
          sx={{
            fontWeight: 600,
            fontSize: '0.7rem',
            height: 20,
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
          }}
        />
        <Typography color="text.secondary" sx={{ fontSize: '0.75rem' }}>
          •
        </Typography>
        <Typography color="text.secondary" sx={{ fontSize: '0.75rem' }}>
          {deliveryDay.toString()}
        </Typography>
      </Box>

      {/* Market Direction Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
        <Tabs
          aria-label={t('bidding.bidding_cascading.review_bids.market_direction_tabs')}
          value={activeTab}
          onChange={handleTabChange}
        >
          <Tab
            disabled={upBids.length === 0}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>{t('bidding.bidding_cascading.review_bids.up_tab', { count: upBids.length })}</span>
                <Chip
                  label={upBids.length}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    backgroundColor: upBids.length > 0 ? 'success.lighter' : 'action.disabledBackground',
                    color: upBids.length > 0 ? 'success.dark' : 'text.disabled',
                  }}
                />
              </Box>
            }
          />
          <Tab
            disabled={downBids.length === 0}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>{t('bidding.bidding_cascading.review_bids.down_tab', { count: downBids.length })}</span>
                <Chip
                  label={downBids.length}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    backgroundColor: downBids.length > 0 ? 'error.lighter' : 'action.disabledBackground',
                    color: downBids.length > 0 ? 'error.dark' : 'text.disabled',
                  }}
                />
              </Box>
            }
          />
        </Tabs>
      </Box>

      {/* Direction-specific content: summary stats + table */}
      <DirectionBidsSection
        activationGroupsMap={activationGroupsMap}
        allErrors={errors}
        bids={currentBids}
        direction={currentDirection}
      />

      {/* Bottom Actions */}
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          justifyContent: 'flex-end',
          alignItems: 'center',
          px: 3,
          py: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Button variant="outlined" onClick={onBackClick}>
          {t('bidding.bidding_cascading.review_bids.back_to_bid_ladder')}
        </Button>
        {canMutateBidding && (
          <CreateBidsButton bids={bids} disabled={isValidating || hasErrors} onSuccess={onSuccess} />
        )}
      </Box>
    </Box>
  )
}

export default ReviewBidsContent
